/**
 * ArchiveBrowser - Two-Level Archive Browser
 *
 * Level 1: Source-filterable, date-filterable, lazy-loaded thread cards
 * Level 2: Single-message navigator with prev/next/start/end
 *
 * @module @humanizer/studio/components/archive/ArchiveBrowser
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useApi, type ArchiveSource, type ArchiveThread } from '../../contexts/ApiContext';
import { useAuth } from '../../contexts/AuthContext';
import { useBufferSync, type StackItem } from '../../contexts/BufferSyncContext';
import { useArchiveApi } from '../../hooks/useArchiveApi';
import { usePanels } from '../../contexts/PanelContext';
import { ChatGPTConnectModal } from '../import/ChatGPTConnectModal';
import { SourceFilterPills, getSourceIcon, type FilterState } from '../ui/SourceFilterPills';
import { useCollectionApi } from '../../hooks/useCollectionApi';
import type { CollectionData } from '../../types/electron';
import { createLogger } from '../../utils/debug';

const log = createLogger('ArchiveBrowser');

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface ArchiveBrowserProps {
  archiveId?: string;
  onSelectContent?: (contentId: string, type: 'node' | 'transcript') => void;
  className?: string;
}

function formatDate(ts?: number): string {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatWordCount(wc: number): string {
  if (wc >= 1000) return `${(wc / 1000).toFixed(1)}k words`;
  return `${wc} words`;
}

type BrowseSort = 'quality' | 'date';

/** Content filter state for format/length/media pills */
interface ContentFilters {
  excludeFormats: string[];   // formats to hide
  onlyFormats: string[];      // formats to include (empty = all)
  minWords: number;           // 10 (default), 50, 100
  hasMedia: boolean | null;   // null = no filter, true = only media, false = no media
}

const DEFAULT_CONTENT_FILTERS: ContentFilters = {
  excludeFormats: [],
  onlyFormats: [],
  minWords: 10,
  hasMedia: null,
};

/** Format display names */
const FORMAT_LABELS: Record<string, string> = {
  text: 'Text',
  markdown: 'Markdown',
  conversation: 'Conversations',
  code: 'Code',
  html: 'HTML',
  json: 'JSON/Data',
};

function getFormatLabel(format: string): string {
  return FORMAT_LABELS[format] || format.charAt(0).toUpperCase() + format.slice(1);
}

/** Tier badge icon mapping */
function getTierBadge(tier?: string): string {
  switch (tier) {
    case 'excellence': return '\u2605';    // star
    case 'polished': return '\u25C6';      // diamond
    case 'raw_gem': return '\u25C7';       // hollow diamond
    case 'needs_refinement': return '\u25CB'; // circle
    default: return '';
  }
}

/** Tier badge CSS class */
function getTierClass(tier?: string): string {
  if (!tier || tier === 'noise') return '';
  return `archive-browser__tier-badge--${tier.replace('_', '-')}`;
}

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function ArchiveBrowser({
  archiveId,
  onSelectContent,
  className = '',
}: ArchiveBrowserProps): React.ReactElement {
  const api = useApi();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { openNodeInContext, stackItems } = useBufferSync();
  const archiveApi = useArchiveApi();
  const { setArchiveTab } = usePanels();

  // Detect platform
  const isElectron = typeof window !== 'undefined' && !!window.isElectron;

  // --- Collection state ---
  const collectionApi = useCollectionApi();
  const [collections, setCollections] = useState<CollectionData[]>([]);
  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(null);

  // --- Source filter state (tri-state: include / exclude / neutral) ---
  const [sources, setSources] = useState<ArchiveSource[]>([]);
  const [sourceFilters, setSourceFilters] = useState<Record<string, 'include' | 'exclude'>>({});

  // --- Date filter state ---
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // --- Thread list state ---
  const [threads, setThreads] = useState<ArchiveThread[]>([]);
  const [threadsTotal, setThreadsTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [sortBy, setSortBy] = useState<BrowseSort>('quality');
  const [serverSearchResults, setServerSearchResults] = useState<ArchiveThread[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Content filter state ---
  const [contentFilters, setContentFilters] = useState<ContentFilters>(DEFAULT_CONTENT_FILTERS);
  const [contentProfile, setContentProfile] = useState<{ formats: Record<string, number>; hasMedia: number; total: number } | null>(null);

  const [error, setError] = useState<string | null>(null);

  // --- ChatGPT import state ---
  const [importUrl, setImportUrl] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Load sources when archive is available
  useEffect(() => {
    if (!archiveApi.isAvailable) return;

    const loadSources = async () => {
      try {
        const result = await archiveApi.getSources();
        setSources(result.sources);
      } catch (err) {
        setError(`Failed to load sources: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    };
    void loadSources();
  }, [archiveApi]);

  // Load collections
  useEffect(() => {
    if (!isElectron) return;
    const loadCollections = async () => {
      try {
        const profile = window.electronLocalAuth
          ? await window.electronLocalAuth.getActiveProfile()
          : null;
        const userId = profile?.id ?? 'local';
        const list = await collectionApi.list(userId);
        setCollections(list);
      } catch {
        // Collections not available — graceful degradation
      }
    };
    void loadCollections();
  }, [isElectron, collectionApi]);

  // Load content profile for filter pills
  useEffect(() => {
    if (!archiveApi.isAvailable) return;
    const loadProfile = async () => {
      try {
        const profile = await archiveApi.getContentProfile();
        setContentProfile(profile);
      } catch {
        // Content profile not available — filters won't show
      }
    };
    void loadProfile();
  }, [archiveApi]);

  // Restore persisted content filters on mount
  useEffect(() => {
    if (!isElectron) return;
    const restore = async () => {
      try {
        const saved = await window.electronAPI?.store.get('browseContentFilters');
        if (saved && typeof saved === 'object') {
          setContentFilters({ ...DEFAULT_CONTENT_FILTERS, ...(saved as Partial<ContentFilters>) });
        }
      } catch {
        // No persisted filters
      }
    };
    void restore();
  }, [isElectron]);

  // When collection changes, apply its criteria as source filters
  const activeCollection = collections.find(c => c.id === activeCollectionId) ?? null;
  useEffect(() => {
    if (!activeCollection) return;
    const criteria = activeCollection.criteria;
    if (criteria?.sourceTypes?.length) {
      const newFilters: Record<string, 'include' | 'exclude'> = {};
      for (const st of criteria.sourceTypes) {
        newFilters[st] = 'include';
      }
      setSourceFilters(newFilters);
    }
    if (criteria?.dateRange?.start) setDateFrom(criteria.dateRange.start);
    if (criteria?.dateRange?.end) setDateTo(criteria.dateRange.end);
  }, [activeCollection]);

  // Collect source type include/exclude lists for reuse
  const { sourceIncludes, sourceExcludes } = useMemo(() => {
    const includes: string[] = [];
    const excludes: string[] = [];
    for (const [type, state] of Object.entries(sourceFilters)) {
      const src = sources.find(s => s.sourceType === type);
      const rawTypes = src?.rawTypes ?? [type];
      if (state === 'include') includes.push(...rawTypes);
      else if (state === 'exclude') excludes.push(...rawTypes);
    }
    return { sourceIncludes: includes, sourceExcludes: excludes };
  }, [sourceFilters, sources]);

  // Extract context node IDs from stack for M4 boost
  const contextNodeIds = useMemo(() => {
    return stackItems
      .map((s: StackItem) => s.sourceNodeId)
      .filter((id): id is string => !!id);
  }, [stackItems]);

  // Persist content filters on change
  const updateContentFilters = useCallback((next: ContentFilters) => {
    setContentFilters(next);
    if (isElectron) {
      // Fire-and-forget persistence
      window.electronAPI?.store.set('browseContentFilters', next);
    }
  }, [isElectron]);

  const resetContentFilters = useCallback(() => {
    setContentFilters(DEFAULT_CONTENT_FILTERS);
    if (isElectron) {
      window.electronAPI?.store.set('browseContentFilters', null);
    }
  }, [isElectron]);

  // Check if any content filters are active (non-default)
  const hasActiveContentFilters = useMemo(() => {
    return contentFilters.excludeFormats.length > 0
      || contentFilters.onlyFormats.length > 0
      || contentFilters.minWords > 10
      || contentFilters.hasMedia !== null;
  }, [contentFilters]);

  // Toggle a format pill through tri-state: neutral -> active (include only) -> excluded -> neutral
  const toggleFormatFilter = useCallback((format: string, altKey: boolean) => {
    setContentFilters(prev => {
      const isExcluded = prev.excludeFormats.includes(format);
      const isActive = prev.onlyFormats.includes(format);

      let next: ContentFilters;

      if (altKey) {
        // Alt+click: directly toggle exclude
        if (isExcluded) {
          next = { ...prev, excludeFormats: prev.excludeFormats.filter(f => f !== format) };
        } else {
          next = {
            ...prev,
            excludeFormats: [...prev.excludeFormats, format],
            onlyFormats: prev.onlyFormats.filter(f => f !== format),
          };
        }
      } else {
        // Click: cycle neutral -> active -> excluded -> neutral
        if (isActive) {
          // active -> excluded
          next = {
            ...prev,
            onlyFormats: prev.onlyFormats.filter(f => f !== format),
            excludeFormats: [...prev.excludeFormats, format],
          };
        } else if (isExcluded) {
          // excluded -> neutral
          next = { ...prev, excludeFormats: prev.excludeFormats.filter(f => f !== format) };
        } else {
          // neutral -> active
          next = { ...prev, onlyFormats: [...prev.onlyFormats, format] };
        }
      }

      if (isElectron) {
        window.electronAPI?.store.set('browseContentFilters', next);
      }
      return next;
    });
  }, [isElectron]);

  // Load threads when source or date filter changes
  const loadThreads = useCallback(async (reset = true) => {
    if (!archiveApi.isAvailable) {
      return;
    }

    if (reset) {
      setIsLoading(true);
      setThreads([]);
    } else {
      setIsLoadingMore(true);
    }

    const offset = reset ? 0 : threads.length;

    try {
      const opts: Record<string, unknown> = {
        limit: PAGE_SIZE,
        offset,
        sortBy,
        hideNoise: true,
      };
      if (sourceIncludes.length > 0) {
        opts.sourceTypes = sourceIncludes;
      }
      if (sourceExcludes.length > 0) {
        opts.sourceTypeExclude = sourceExcludes;
      }
      if (dateFrom) opts.dateFrom = dateFrom;
      if (dateTo) opts.dateTo = dateTo;

      // Content filters
      if (contentFilters.excludeFormats.length > 0) opts.excludeFormats = contentFilters.excludeFormats;
      if (contentFilters.onlyFormats.length > 0) opts.onlyFormats = contentFilters.onlyFormats;
      if (contentFilters.minWords > 10) opts.minWords = contentFilters.minWords;
      if (contentFilters.hasMedia !== null) opts.hasMedia = contentFilters.hasMedia;

      const result = await archiveApi.getThreads(opts);

      if (reset) {
        setThreads(result.threads);
      } else {
        setThreads((prev) => [...prev, ...result.threads]);
      }
      setThreadsTotal(result.total);
      setHasMore(result.hasMore);
    } catch (err) {
      setError(`Failed to load threads: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [archiveApi, sourceIncludes, sourceExcludes, sortBy, dateFrom, dateTo, contentFilters, threads.length]);

  // Reload on filter change
  useEffect(() => {
    if (!archiveApi.isAvailable) return;
    // Clear server search results when filters change
    setServerSearchResults(null);
    setSearchFilter('');
    void loadThreads(true);
  }, [sourceFilters, dateFrom, dateTo, sortBy, contentFilters, archiveApi.isAvailable]); // eslint-disable-line react-hooks/exhaustive-deps

  // IntersectionObserver for lazy loading
  useEffect(() => {
    if (!archiveApi.isAvailable || !sentinelRef.current || !hasMore || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !isLoadingMore) {
          void loadThreads(false);
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, loadThreads, archiveApi.isAvailable]);

  // If archive not available, show appropriate message
  if (!archiveApi.isAvailable) {
    return (
      <div className={`archive-browser ${className}`}>
        <div className="archive-browser__electron-notice">
          {isElectron ? (
            <>
              <p>Archive not connected.</p>
              <p>Please configure PostgreSQL in the setup panel.</p>
            </>
          ) : (
            <>
              <p>Please sign in to access archives.</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // Debounced server-side search (M3: proximity-weighted)
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    const query = searchFilter.trim();
    if (!query) {
      setServerSearchResults(null);
      setIsSearching(false);
      return;
    }

    // Don't trigger server search for very short queries
    if (query.length < 2) {
      setServerSearchResults(null);
      return;
    }

    setIsSearching(true);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const searchOpts: Record<string, unknown> = { limit: 50 };
        if (sourceIncludes.length > 0) searchOpts.sourceTypes = sourceIncludes;
        if (sourceExcludes.length > 0) searchOpts.sourceTypeExclude = sourceExcludes;
        if (contextNodeIds.length > 0) searchOpts.contextNodeIds = contextNodeIds;
        // Content filters
        if (contentFilters.excludeFormats.length > 0) searchOpts.excludeFormats = contentFilters.excludeFormats;
        if (contentFilters.onlyFormats.length > 0) searchOpts.onlyFormats = contentFilters.onlyFormats;
        if (contentFilters.minWords > 10) searchOpts.minWords = contentFilters.minWords;
        if (contentFilters.hasMedia !== null) searchOpts.hasMedia = contentFilters.hasMedia;

        const result = await archiveApi.searchBrowse(query, searchOpts);
        setServerSearchResults(result.results);
      } catch {
        // Fallback: client-side filter
        setServerSearchResults(null);
      } finally {
        setIsSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchFilter, archiveApi, sourceIncludes, sourceExcludes, contextNodeIds, contentFilters]);

  // Use server search results when available, otherwise show browse threads
  const filteredThreads = useMemo(() => {
    if (serverSearchResults !== null) return serverSearchResults;
    return threads;
  }, [threads, serverSearchResults]);

  // --- Thread click: open in workspace via openNodeInContext ---
  const openThread = useCallback(async (thread: ArchiveThread) => {
    const threadList = filteredThreads.map(t => t.id);
    const threadListIndex = threadList.indexOf(thread.id);

    try {
      await openNodeInContext({
        nodeId: thread.id,
        threadList: threadListIndex >= 0 ? threadList : undefined,
        threadListIndex: threadListIndex >= 0 ? threadListIndex : undefined,
      });
    } catch (err) {
      log.error('Failed to open thread:', err);
    }
  }, [openNodeInContext, filteredThreads]);


  // --- ChatGPT URL import handler ---
  const handleImportUrl = useCallback(async (url: string) => {
    if (!url.trim()) return;

    // Quick check: is this a chatgpt.com URL?
    const isChatGPT = /chatgpt\.com\/(c|share)\/|chat\.openai\.com\/(c|share)\//.test(url);
    if (!isChatGPT) {
      setError('Only ChatGPT URLs are supported (chatgpt.com/c/...)');
      return;
    }

    setIsImporting(true);
    setError(null);

    try {
      // Check if token exists first
      const { connected } = await api.chatgptImport.tokenStatus();
      if (!connected) {
        setIsImporting(false);
        setShowConnectModal(true);
        return;
      }

      const result = await api.chatgptImport.fetchConversation(url);
      setImportUrl('');

      // Reload threads and navigate to imported thread
      await loadThreads(true);

      // Open imported thread in workspace
      await openNodeInContext({ nodeId: result.threadId });
    } catch (err: any) {
      const body = err?.response ? await err.response.json().catch(() => null) : null;
      if (body?.tokenExpired) {
        setShowConnectModal(true);
      } else {
        setError(body?.error ?? (err instanceof Error ? err.message : 'Import failed'));
      }
    } finally {
      setIsImporting(false);
    }
  }, [api.chatgptImport, api.archive, loadThreads]);

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER: Error Banner
  // ═══════════════════════════════════════════════════════════════════════

  const errorBanner = error ? (
    <div className="archive-browser__error" role="alert">
      <span>{error}</span>
      <button className="archive-browser__error-dismiss" onClick={() => setError(null)} aria-label="Dismiss error">✕</button>
    </div>
  ) : null;

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER: Thread List
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <div className={`archive-browser ${className}`}>
      {errorBanner}

      {/* ChatGPT Connect Modal */}
      {showConnectModal && (
        <ChatGPTConnectModal
          onClose={() => setShowConnectModal(false)}
          onConnected={() => {
            // Re-trigger the import if a URL was pending
            if (importUrl.trim()) {
              void handleImportUrl(importUrl);
            }
          }}
        />
      )}

      {/* URL Import Bar (cloud only - requires API) */}
      {!isElectron && (
        <div className="archive-browser__import-bar">
          <input
            type="url"
            className="archive-browser__import-input"
            placeholder="Paste a ChatGPT URL to import..."
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleImportUrl(importUrl);
            }}
            disabled={isImporting}
            aria-label="Import conversation URL"
          />
          {isImporting ? (
            <div className="archive-browser__import-spinner">
              <div className="panel__loading-spinner" />
            </div>
          ) : importUrl.trim() ? (
            <button
              className="archive-browser__import-btn"
              onClick={() => void handleImportUrl(importUrl)}
            >
              Import
            </button>
          ) : null}
        </div>
      )}

      {/* Collection Selector */}
      {collections.length > 0 && (
        <div className="archive-browser__collection-bar">
          <select
            className="archive-browser__collection-select"
            value={activeCollectionId ?? ''}
            onChange={(e) => {
              const id = e.target.value || null;
              setActiveCollectionId(id);
              if (!id) {
                setSourceFilters({});
                setDateFrom('');
                setDateTo('');
              }
            }}
          >
            <option value="">All content</option>
            {collections.map((c) => (
              <option key={c.id} value={c.id}>
                {c.icon ? `${c.icon} ` : ''}{c.name} ({c.nodeCount})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Source Filter Pills (tri-state: click=include, alt+click=exclude, click again=neutral) */}
      <SourceFilterPills
        sources={sources}
        filters={sourceFilters}
        onFiltersChange={setSourceFilters}
        mode="tri-state"
        totalCount={threadsTotal}
      />

      {/* Date Range Filter */}
      <div className="archive-browser__date-range">
        <input
          type="date"
          className="archive-browser__date-input"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          aria-label="From date"
        />
        <span className="archive-browser__date-sep">—</span>
        <input
          type="date"
          className="archive-browser__date-input"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          aria-label="To date"
        />
        {(dateFrom || dateTo) && (
          <button
            className="archive-browser__date-clear"
            onClick={() => { setDateFrom(''); setDateTo(''); }}
            aria-label="Clear date filter"
          >
            ✕
          </button>
        )}
      </div>

      {/* Content Filter Pills */}
      {contentProfile && Object.keys(contentProfile.formats).length > 1 && (
        <div className="archive-browser__content-filters">
          {/* Format pills — only show formats that exist */}
          {Object.entries(contentProfile.formats)
            .sort(([, a], [, b]) => b - a)
            .map(([format, count]) => {
              const isExcluded = contentFilters.excludeFormats.includes(format);
              const isActive = contentFilters.onlyFormats.includes(format);
              const pillClass = isActive
                ? 'archive-browser__content-pill archive-browser__content-pill--active'
                : isExcluded
                  ? 'archive-browser__content-pill archive-browser__content-pill--excluded'
                  : 'archive-browser__content-pill';
              return (
                <button
                  key={format}
                  className={pillClass}
                  onClick={(e) => toggleFormatFilter(format, e.altKey)}
                  title={`${getFormatLabel(format)} (${count}) — click to include, alt+click to exclude`}
                >
                  {getFormatLabel(format)} {isExcluded ? '\u2715' : ''}
                  <span className="archive-browser__content-pill__count">{count}</span>
                </button>
              );
            })}

          {/* Has Media pill */}
          {contentProfile.hasMedia > 0 && (
            <button
              className={`archive-browser__content-pill${
                contentFilters.hasMedia === true
                  ? ' archive-browser__content-pill--active'
                  : contentFilters.hasMedia === false
                    ? ' archive-browser__content-pill--excluded'
                    : ''
              }`}
              onClick={() => {
                const next = contentFilters.hasMedia === null
                  ? true
                  : contentFilters.hasMedia === true
                    ? false
                    : null;
                updateContentFilters({ ...contentFilters, hasMedia: next });
              }}
              title={`Has media (${contentProfile.hasMedia}) — click to cycle: all → media only → no media → all`}
            >
              Media {contentFilters.hasMedia === false ? '\u2715' : ''}
              <span className="archive-browser__content-pill__count">{contentProfile.hasMedia}</span>
            </button>
          )}

          {/* Min words pills */}
          {[50, 100].map((threshold) => (
            <button
              key={threshold}
              className={`archive-browser__content-pill${
                contentFilters.minWords >= threshold ? ' archive-browser__content-pill--active' : ''
              }`}
              onClick={() => {
                const next = contentFilters.minWords >= threshold ? 10 : threshold;
                updateContentFilters({ ...contentFilters, minWords: next });
              }}
              title={`Show only posts with ${threshold}+ words`}
            >
              {threshold}+ words
            </button>
          ))}

          {/* Reset button */}
          {hasActiveContentFilters && (
            <button
              className="archive-browser__filter-reset"
              onClick={resetContentFilters}
              title="Reset all content filters"
            >
              Reset
            </button>
          )}
        </div>
      )}

      {/* Sort Toggle + Search */}
      <div className="archive-browser__sort-bar">
        <button
          className={`archive-browser__sort-btn${sortBy === 'quality' ? ' archive-browser__sort-btn--active' : ''}`}
          onClick={() => setSortBy('quality')}
        >
          Best
        </button>
        <button
          className={`archive-browser__sort-btn${sortBy === 'date' ? ' archive-browser__sort-btn--active' : ''}`}
          onClick={() => setSortBy('date')}
        >
          Recent
        </button>
      </div>

      {/* Search/Filter */}
      <div className="archive-search-input">
        <div className="archive-search-input__field">
          <span className="archive-search-input__icon">🔍</span>
          <input
            type="text"
            className="archive-search-input__input"
            placeholder="Search conversations..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            aria-label="Search archive"
          />
          {isSearching && (
            <div className="archive-browser__search-spinner">
              <div className="panel__loading-spinner" />
            </div>
          )}
          {searchFilter && !isSearching && (
            <button
              className="archive-search-input__clear"
              onClick={() => setSearchFilter('')}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Thread count header */}
      {!isLoading && filteredThreads.length > 0 && (
        <div className="archive-browser__list-header">
          {serverSearchResults !== null
            ? `${filteredThreads.length} results`
            : `${threadsTotal.toLocaleString()} conversations`}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="panel__loading">
          <div className="panel__loading-spinner" />
        </div>
      )}

      {/* Empty */}
      {!isLoading && filteredThreads.length === 0 && (
        <div className="panel__empty">
          <div className="panel__empty-icon">📂</div>
          <div className="panel__empty-title">
            {searchFilter ? 'No Matches' : 'No Conversations'}
          </div>
          <div className="panel__empty-description">
            {searchFilter
              ? 'Try different filter terms'
              : isElectron
                ? 'Import conversations using the Import tab'
                : 'Select a source or adjust filters'}
          </div>
        </div>
      )}

      {/* Thread Cards */}
      {!isLoading && filteredThreads.length > 0 && (
        <div className="archive-browser__thread-list" ref={listRef}>
          {filteredThreads.map((thread) => (
            <button
              key={thread.id}
              className="archive-browser__thread-card"
              onClick={() => void openThread(thread)}
            >
              <div className="archive-browser__card-header">
                {thread.excellenceTier && thread.excellenceTier !== 'noise' && thread.excellenceTier !== 'needs_refinement' && (
                  <span
                    className={`archive-browser__tier-badge ${getTierClass(thread.excellenceTier)}`}
                    title={`${thread.excellenceTier} (${thread.excellenceScore ?? '?'})`}
                  >
                    {getTierBadge(thread.excellenceTier)}
                  </span>
                )}
                <span className="archive-browser__card-title">{thread.title}</span>
              </div>
              <div className="archive-browser__card-meta">
                <span className="archive-browser__card-source">
                  {getSourceIcon(thread.sourceType)} {thread.sourceType}
                </span>
                {thread.sourceCreatedAt && (
                  <>
                    <span className="archive-browser__meta-sep">·</span>
                    <span>{formatDate(thread.sourceCreatedAt)}</span>
                  </>
                )}
                <span className="archive-browser__meta-sep">·</span>
                <span>{formatWordCount(thread.wordCount)}</span>
              </div>
              {thread.textPreview && (
                <div className="archive-browser__card-preview">{thread.textPreview}</div>
              )}
              <div className="archive-browser__card-actions">
                <button
                  className="archive-browser__more-like"
                  onClick={(e) => {
                    e.stopPropagation();
                    setArchiveTab('drift');
                  }}
                  title="Find similar content"
                >
                  More like this
                </button>
              </div>
            </button>
          ))}

          {/* Lazy load sentinel (only when browsing, not searching) */}
          {hasMore && serverSearchResults === null && (
            <div ref={sentinelRef} className="archive-browser__sentinel">
              {isLoadingMore && (
                <div className="panel__loading">
                  <div className="panel__loading-spinner" />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ArchiveBrowser;
